import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import {
  AccountSchema,
  AssetClassSchema,
  AssetSchema,
  ProcurementListsSchema,
  ProjectSchema,
  TaxCodeSchema,
  type AccountDto,
  type AssetClassDto,
  type AssetDto,
  type ProjectDto,
} from '@taqon/contracts';
import { getData, getPage } from '../../shared/api/client';
import { recordPath } from '../../shared/records/routes';
import { hasCapability, useSession } from '../../shared/session/session';
import { ActiveProjectPicker } from '../crm/common';
import { RecordLink } from '../finance/detail';
import { AccountPicker, MasterSelect, SearchPicker, useActiveMasters } from '../finance/lookups';

/**
 * Pieces the purchasing pages share (ERD §7.2): the API paths, the status vocabulary, links that
 * open only where the session may read the target, and the target-account-and-dimensions fields a
 * request line or an emergency order line records (FRD-PRC-019, PRC-020). Every picker searches
 * the server; nothing here decides what the API accepts.
 */

export const PR_API = '/api/v1/procurement/purchase-requests';
export const MY_PR_API = '/api/v1/procurement/my-purchase-requests';
export const PO_API = '/api/v1/procurement/purchase-orders';
export const GR_API = '/api/v1/procurement/goods-receipts';

export const PR_TYPE = 'procurement.purchase_request';
export const PO_TYPE = 'procurement.purchase_order';
export const GR_TYPE = 'procurement.goods_receipt';

export const PR_STATUS_LABELS: Readonly<Record<string, string>> = {
  draft: 'Draft',
  submitted: 'In approval',
  approved: 'Approved',
  rejected: 'Rejected',
  returned: 'Returned',
  converted: 'Converted',
  cancelled: 'Cancelled',
};

export const PO_STATUS_LABELS: Readonly<Record<string, string>> = {
  draft: 'Draft',
  submitted: 'In approval',
  approved: 'Approved',
  rejected: 'Rejected',
  returned: 'Returned',
  issued: 'Issued',
  partially_received: 'Partially received',
  received: 'Received',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

/** Prepared or sent back: the states in which a document is edited and (re)submitted. */
export const EDITABLE = ['draft', 'returned', 'rejected'];

/** Whether an order came from an approved request or is the emergency exception (FRD-PRC-025). */
export function OriginChip({ origin }: { origin: string }) {
  return origin === 'emergency' ? (
    <Chip size="small" color="warning" label="Emergency (no purchase request)" />
  ) : (
    <Chip size="small" variant="outlined" label="From purchase request" />
  );
}

/** A related purchasing record, linked where the session may open it. */
export function PurchasingLink({
  type,
  id,
  children,
}: {
  type: string;
  id: string;
  children: ReactNode;
}) {
  const session = useSession();
  const path = recordPath(type, id);
  return path !== null && hasCapability(session.data, `${type}:view`) ? (
    <RecordLink to={path}>{children}</RecordLink>
  ) : (
    <>{children}</>
  );
}

/** The purchase request category values (ERD §7.2), inactive ones for history. */
export function useProcurementLists() {
  const session = useSession();
  return useQuery({
    queryKey: ['procurement', 'lists'],
    enabled: hasCapability(session.data, `${PR_TYPE}:view`),
    queryFn: () => getData('/api/v1/procurement/lists', ProcurementListsSchema),
  });
}

/** Today in the business time zone (SDD §5.1). */
export function useBusinessToday(): string {
  const session = useSession();
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: session.data?.businessTimezone ?? 'Asia/Dubai',
  }).format(new Date());
}

// ------------------------------------------------------------------------ target and dimensions

/** A line's target account and the dimensions its COA flags ask for (FRD-PRC-020). */
export interface TargetDraft {
  account: AccountDto | null;
  costCenterId: string;
  project: ProjectDto | null;
  asset: AssetDto | null;
}

export const emptyTarget = (): TargetDraft => ({
  account: null,
  costCenterId: '',
  project: null,
  asset: null,
});

/** The target as the API takes it: only the dimensions chosen are sent. */
export function targetPayload(target: TargetDraft) {
  return {
    accountId: target.account?.id ?? '',
    ...(target.costCenterId !== '' ? { costCenterId: target.costCenterId } : {}),
    ...(target.project !== null ? { projectId: target.project.id } : {}),
    ...(target.asset !== null ? { assetId: target.asset.id } : {}),
  };
}

/** What stops a target being sent, in words. */
export function targetProblems(target: TargetDraft, line: string): string[] {
  const out: string[] = [];
  const requires = target.account?.requires;
  if (target.account === null) out.push(`${line}: choose the target account.`);
  if (requires?.costCenter === true && target.costCenterId === '') {
    out.push(`${line}: choose a cost centre (required by the account).`);
  }
  if (requires?.project === true && target.project === null) {
    out.push(`${line}: choose a project (required by the account).`);
  }
  if (requires?.asset === true && target.asset === null) {
    out.push(`${line}: choose an asset (required by the account).`);
  }
  if (requires?.client === true || requires?.employee === true) {
    out.push(
      `${line}: account ${target.account?.code ?? ''} requires a dimension a purchasing line ` +
        'does not record; choose another account.',
    );
  }
  return out;
}

/**
 * Rebuilds a saved line's target for editing, reading each reference from its master where the
 * session may; a reference the session cannot read is left empty for the person to choose again.
 */
export async function loadTarget(
  line: {
    accountCode: string | null;
    costCenterId: string | null;
    projectId: string | null;
    assetId: string | null;
  },
  can: (capability: string) => boolean,
): Promise<TargetDraft> {
  const account =
    line.accountCode === null || !can('ledger.account:view')
      ? null
      : ((
          await getPage(
            `/api/v1/ledger/accounts?pageSize=1&sort=code&filter[code]=${encodeURIComponent(line.accountCode)}`,
            AccountSchema,
          ).catch(() => ({ items: [] }))
        ).items.find((a) => a.code === line.accountCode) ?? null);
  const project =
    line.projectId === null || !can('projects.project:view')
      ? null
      : await getData(`/api/v1/projects/${line.projectId}`, ProjectSchema).catch(() => null);
  const asset =
    line.assetId === null || !can('assets.asset:view')
      ? null
      : await getData(`/api/v1/assets/assets/${line.assetId}`, AssetSchema).catch(() => null);
  return { account, costCenterId: line.costCenterId ?? '', project, asset };
}

export async function loadAssetClass(
  id: string | null,
  can: (capability: string) => boolean,
): Promise<AssetClassDto | null> {
  return id === null || !can('assets.asset_class:view')
    ? null
    : getData(`/api/v1/assets/asset-classes/${id}`, AssetClassSchema).catch(() => null);
}

const search = (term: string) => (term === '' ? '' : `&filter[search]=${encodeURIComponent(term)}`);
const describeClass = (c: AssetClassDto) => `${c.code} — ${c.name}`;
const describeAsset = (a: AssetDto) => `${a.code} — ${a.name}`;

/** Active asset classes by code or name (FRD-PRC-026). */
export function AssetClassPicker(props: {
  value: AssetClassDto | null;
  onChange: (value: AssetClassDto | null) => void;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  helperText?: string | undefined;
}) {
  return (
    <SearchPicker
      label="Asset class"
      queryKey="asset-classes-active"
      schema={AssetClassSchema}
      path={(term) =>
        `/api/v1/assets/asset-classes?pageSize=25&sort=code&filter[status]=active${search(term)}`
      }
      describe={describeClass}
      {...props}
    />
  );
}

/** Registered assets by Asset ID or name, for the Asset dimension (FRD-PRC-020). */
export function AssetPicker(props: {
  value: AssetDto | null;
  onChange: (value: AssetDto | null) => void;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: boolean | undefined;
  helperText?: string | undefined;
}) {
  return (
    <SearchPicker
      label="Asset"
      queryKey="assets-any"
      schema={AssetSchema}
      path={(term) => `/api/v1/assets/assets?pageSize=25&sort=code${search(term)}`}
      describe={describeAsset}
      {...props}
    />
  );
}

/** The active input tax codes, for a request line's VAT treatment (FRD-PRC-019). */
export function TaxCodeSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const session = useSession();
  const canSee = hasCapability(session.data, 'tax.tax_code:view');
  const codes = useQuery({
    queryKey: ['tax-codes', 'input-active'],
    enabled: canSee,
    queryFn: () =>
      getPage(
        '/api/v1/tax/tax-codes?pageSize=200&sort=code&filter[status]=active&filter[direction]=input',
        TaxCodeSchema,
      ),
  });
  return (
    <TextField
      select
      size="small"
      label="VAT treatment (tax code)"
      value={value}
      disabled={!canSee}
      helperText={canSee ? undefined : 'Needs permission to view tax codes'}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      sx={{ minWidth: 220 }}
    >
      <MenuItem value="">Not recorded</MenuItem>
      {(codes.data?.items ?? []).map((code) => (
        <MenuItem key={code.id} value={code.id}>
          {code.code} — {code.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

/**
 * The target account, with the line's cost centre and project (FRD-PRC-019: a requisition records
 * project and cost center) — required where the account's COA flags require them, optional
 * otherwise — and the asset where the account requires the Asset dimension (FRD-PRC-020).
 */
export function TargetFields({
  target,
  onChange,
}: {
  target: TargetDraft;
  onChange: (target: TargetDraft) => void;
}) {
  const session = useSession();
  const can = (capability: string) => hasCapability(session.data, capability);
  const costCentres = useActiveMasters(
    '/api/v1/organization/cost-centers',
    can('organization.cost_center:view'),
  );
  const requires = target.account?.requires;
  const needs = [
    requires?.costCenter === true && 'Cost centre',
    requires?.project === true && 'Project',
    requires?.asset === true && 'Asset',
  ].filter((x): x is string => typeof x === 'string');
  return (
    <Stack spacing={2}>
      {can('ledger.account:view') ? (
        <AccountPicker
          value={target.account}
          onChange={(account) => {
            onChange({ ...emptyTarget(), account });
          }}
          helperText={
            target.account === null
              ? 'Search by code or name'
              : needs.length === 0
                ? 'No cost centre, project or asset required'
                : `Requires: ${needs.join(', ')}`
          }
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          Choosing the target account needs permission to view the chart of accounts.
        </Typography>
      )}
      {target.account !== null && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          }}
        >
          <MasterSelect
            label="Cost centre"
            required={requires?.costCenter === true}
            value={target.costCenterId}
            disabled={!can('organization.cost_center:view')}
            error={requires?.costCenter === true && target.costCenterId === ''}
            helperText={
              can('organization.cost_center:view')
                ? requires?.costCenter === true
                  ? 'Required by the account'
                  : 'Optional'
                : 'Needs permission to view cost centres'
            }
            options={costCentres.data?.items ?? []}
            onChange={(costCenterId) => {
              onChange({ ...target, costCenterId });
            }}
          />
          {can('projects.project:view') ? (
            <ActiveProjectPicker
              label="Project"
              value={target.project}
              helperText={requires?.project === true ? 'Required by the account' : 'Optional'}
              onChange={(project) => {
                onChange({ ...target, project });
              }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {requires?.project === true
                ? 'The account requires a project; choosing one needs permission to view projects'
                : 'Choosing a project needs permission to view projects'}
            </Typography>
          )}
          {requires?.asset === true && (
            <AssetPicker
              value={target.asset}
              required
              disabled={!can('assets.asset:view')}
              helperText={can('assets.asset:view') ? undefined : 'Needs permission to view assets'}
              onChange={(asset) => {
                onChange({ ...target, asset });
              }}
            />
          )}
        </Box>
      )}
    </Stack>
  );
}

/** The dimensions a saved line carries, in words, for tables. */
export function dimensionText(line: {
  costCenterLabel: string | null;
  projectLabel: string | null;
  assetLabel: string | null;
}): string {
  return (
    [
      line.costCenterLabel !== null && `Cost centre ${line.costCenterLabel}`,
      line.projectLabel !== null && `Project ${line.projectLabel}`,
      line.assetLabel !== null && `Asset ${line.assetLabel}`,
    ]
      .filter((x): x is string => typeof x === 'string')
      .join(' · ') || '—'
  );
}
